import {Component} from "./component";

interface RagDocument {
    id: number;
    entity_id: number;
    entity_type: string;
    entity_title: string;
    breadcrumb: string;
    status: string;
    status_label: string;
    status_color: string;
    chunks_count: number;
    indexed_at: string | null;
    error: string | null;
}

interface RagResponse {
    data: RagDocument[];
    stats: {
        indexed: number;
        pending: number;
        indexing: number;
        failed: number;
        total: number;
    };
    current_page: number;
    last_page: number;
}

export class RagIndexTable extends Component {
    private searchInput!: HTMLInputElement;
    private tableBody!: HTMLElement;
    private paginationEl!: HTMLElement;
    private feedbackEl!: HTMLElement;
    
    private statIndexed!: HTMLElement;
    private statPending!: HTMLElement;
    private statIndexing!: HTMLElement;
    private statFailed!: HTMLElement;
    private statTotal!: HTMLElement;

    private currentPage: number = 1;
    private searchTerm: string = '';
    private debounceTimer?: number;
    private feedbackTimer?: number;

    setup() {
        this.searchInput = this.$refs.search as HTMLInputElement;
        this.tableBody = this.$refs.tableBody;
        this.paginationEl = this.$refs.pagination;
        this.feedbackEl = this.$refs.feedback;

        this.statIndexed = this.$refs.statIndexed;
        this.statPending = this.$refs.statPending;
        this.statIndexing = this.$refs.statIndexing;
        this.statFailed = this.$refs.statFailed;
        this.statTotal = this.$refs.statTotal;

        // Listen for search input
        if (this.searchInput) {
            this.searchInput.addEventListener('input', () => {
                window.clearTimeout(this.debounceTimer);
                this.searchTerm = this.searchInput.value.trim();
                this.debounceTimer = window.setTimeout(() => this.loadDocuments(1), 400);
            });
        }

        // Action buttons (reindex all)
        const btnKeep = this.$refs.btnReindexKeep;
        if (btnKeep) btnKeep.addEventListener('click', () => this.reindexAll(false));

        const btnClear = this.$refs.btnReindexClear;
        if (btnClear) btnClear.addEventListener('click', () => this.reindexAll(true));

        // Initial load
        this.loadDocuments(1);
    }

    async loadDocuments(page: number = 1) {
        this.currentPage = page;
        const baseUrl = this.$opts.urlDocuments;
        if (!baseUrl) return;

        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('page', page.toString());
        if (this.searchTerm) url.searchParams.set('search', this.searchTerm);

        this.setTableLoading(true);

        try {
            const res = await fetch(url.toString(), {
                headers: {'X-Requested-With': 'XMLHttpRequest'}
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

            const json: RagResponse = await res.json();
            this.renderStats(json.stats);
            this.renderTable(json.data);
            this.renderPagination(json.current_page, json.last_page);
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            this.showTableError(msg);
            this.showFeedback('Erro ao carregar documentos: ' + msg, 'danger');
        } finally {
            this.setTableLoading(false);
        }
    }

    private renderStats(stats: RagResponse['stats']) {
        const s = stats || ({} as RagResponse['stats']);
        if (this.statIndexed) this.statIndexed.textContent = `${s.indexed || 0} indexados`;
        if (this.statPending) this.statPending.textContent = `${s.pending || 0} pendentes`;
        if (this.statFailed) this.statFailed.textContent = `${s.failed || 0} falharam`;
        if (this.statTotal) this.statTotal.textContent = `${s.total || 0} total`;

        // "Indexando" só aparece com algo em andamento — evita um selo sempre zerado.
        if (this.statIndexing) {
            const running = s.indexing || 0;
            this.statIndexing.textContent = `${running} indexando`;
            this.statIndexing.style.display = running > 0 ? '' : 'none';
        }
    }

    private renderTable(rows: RagDocument[]) {
        if (!this.tableBody) return;

        if (rows.length === 0) {
            this.tableBody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:2rem;color:#888;">Nenhum documento encontrado.</td></tr>';
            return;
        }

        const typeLabels: Record<string, string> = {
            page: 'Página',
            chapter: 'Capítulo',
            book: 'Livro',
            attachment: 'Anexo',
        };

        this.tableBody.innerHTML = rows.map(row => {
            const colorMap: Record<string, string> = {
                indexed: 'rag-badge-pos',
                failed: 'rag-badge-neg',
                indexing: 'rag-badge-warn',
                pending: 'rag-badge-muted',
            };
            const tagClass = colorMap[row.status] || 'rag-badge-muted';
            const errorTip = row.error ? ` title="${this.escHtml(row.error)}"` : '';
            const typeLabel = typeLabels[row.entity_type] || row.entity_type;

            return `<tr>
                <td><span class="rag-badge ${tagClass}"${errorTip}>${this.escHtml(row.status_label)}</span></td>
                <td>
                    ${this.escHtml(row.entity_title)}
                    <small style="color:#888; display:block; margin-top:2px;">${this.escHtml(typeLabel)}</small>
                </td>
                <td style="color:#888;">${this.escHtml(row.breadcrumb) || '—'}</td>
                <td style="text-align:center;">${row.chunks_count || '—'}</td>
                <td>${this.escHtml(row.indexed_at || '') || '—'}</td>
                <td style="text-align:center;">
                    <button type="button" class="button small outline rag-reindex-btn"
                            data-type="${this.escHtml(row.entity_type)}"
                            data-id="${row.entity_id}"
                            title="Reindexar este documento">
                        &#8635;
                    </button>
                </td>
            </tr>`;
        }).join('');

        this.tableBody.querySelectorAll('.rag-reindex-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const b = btn as HTMLButtonElement;
                this.reindexOne(b.dataset.type!, b.dataset.id!, b);
            });
        });
    }

    private renderPagination(current: number, last: number) {
        if (!this.paginationEl) return;
        if (last <= 1) {
            this.paginationEl.innerHTML = '';
            return;
        }

        const pages: (number | string)[] = [];
        for (let i = 1; i <= last; i++) {
            if (i === 1 || i === last || Math.abs(i - current) <= 2) {
                pages.push(i);
            } else if (pages[pages.length - 1] !== '...') {
                pages.push('...');
            }
        }

        this.paginationEl.innerHTML = pages.map(p => {
            if (p === '...') return '<span style="padding:4px 6px;">…</span>';
            const active = p === current ? 'style="font-weight:bold;"' : '';
            return `<button type="button" class="button small outline rag-page-btn" data-page="${p}" ${active}>${p}</button>`;
        }).join('');

        this.paginationEl.querySelectorAll('.rag-page-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const p = (btn as HTMLElement).dataset.page;
                if (p) this.loadDocuments(parseInt(p));
            });
        });
    }

    private async reindexOne(type: string, id: string, btn: HTMLButtonElement) {
        btn.disabled = true;
        const originalText = btn.textContent;
        btn.textContent = '...';

        try {
            const url = this.$opts.urlReindexOne.replace('{type}', type).replace('{id}', id);
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': this.getCsrf(),
                    'X-Requested-With': 'XMLHttpRequest'
                },
            });

            // Sem esta checagem, uma resposta 403/500 (que vem em HTML) quebrava no
            // res.json() e o usuário via um erro de parse em vez do motivo real.
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }

            const json = await res.json();
            this.showFeedback(json.message || 'Enfileirado.', 'success');
            setTimeout(() => this.loadDocuments(this.currentPage), 1200);
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            this.showFeedback('Erro: ' + msg, 'danger');
        } finally {
            btn.disabled = false;
            btn.textContent = originalText;
        }
    }

    private async reindexAll(clear: boolean) {
        const confirmMsg = clear 
            ? 'Isso vai LIMPAR todos os vetores do Qdrant e reindexar do zero. Confirma?' 
            : 'Reindexar todos os documentos sem limpar vetores existentes. Confirma?';
        
        if (!window.confirm(confirmMsg)) return;

        try {
            const res = await fetch(this.$opts.urlReindexAll, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': this.getCsrf(),
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({clear}),
            });

            if (!res.ok && res.status !== 500) {
                throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }

            const json = await res.json();
            if (json.error) {
                this.showFeedback('Erro: ' + json.error, 'danger');
            } else {
                this.showFeedback(json.message, 'success');
                setTimeout(() => this.loadDocuments(1), 1500);
            }
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            this.showFeedback('Erro: ' + msg, 'danger');
        }
    }

    private setTableLoading(loading: boolean) {
        if (!this.tableBody) return;
        if (loading) {
            this.tableBody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:2rem;color:#888;">Carregando...</td></tr>';
        }
    }

    private showTableError(msg: string) {
        if (this.tableBody) {
            this.tableBody.innerHTML = `<tr><td colspan="6" style="text-align:center;padding:2rem;color:#d9534f;">${this.escHtml(msg)}</td></tr>`;
        }
    }

    // Variantes de callout do BookStack: success/danger/warning/info ('pos'/'neg' não existem).
    private showFeedback(msg: string, type: 'success' | 'danger' = 'success') {
        if (!this.feedbackEl) {
            if (window.$events) {
                type === 'success' ? window.$events.success(msg) : window.$events.error(msg);
            }
            return;
        }

        window.clearTimeout(this.feedbackTimer);

        this.feedbackEl.className = `callout ${type}`;
        this.feedbackEl.textContent = msg;
        this.feedbackEl.style.display = 'block';

        this.feedbackTimer = window.setTimeout(() => {
            if (this.feedbackEl) this.feedbackEl.style.display = 'none';
        }, 5000);
    }

    private escHtml(str: string): string {
        return String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }

    private getCsrf(): string {
        return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    }
}
